import { useEffect, useState } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";
import _ from "lodash";
import Link from "next/link";
import toast from "react-hot-toast";
import { FiCopy } from "react-icons/fi";
import styled from "styled-components";

import { getRequestMessage, getRequestStatusAndProgress } from "../../utils/getRequestStatus";
import AuthrorisedBlockchainTransactionsBanner from "../AuthrorisedBlockchainTransactionsBanner";

import { ClickableText } from "~/components/Button";
import * as DetailList from "~/components/DetailList";
import { Box, BoxDanger, Padding, Section } from "~/components/Elements";
import * as Grid from "~/components/Grid";
import HasPermission from "~/components/HasPermission";
import * as Progress from "~/components/Progress";
import { ViewerPermissions } from "~/contexts/ViewerContext";
import { getCountryNames } from "~/data/countries";
import { useVaspUsersInfo } from "~/endpoints/auth";
import { useGetVaspSettings, useTfSimpleVaspInfo } from "~/endpoints/trustFramework";
import { VaspSettingsPayload } from "~/endpoints/trustFramework/types";
import { TransactionWrap } from "~/hooks/use-transaction";
import useCurrentViewerPermissions from "~/hooks/useCurrentViewerPermissions";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID";
import { useAllVASPs } from "~/templates/rules/AdvancedRules/utils";
import { RuleCheck } from "~/templates/rulesEditor/types";
import { getActionText, getProgress } from "~/templates/transactions/TransactionsPage/utils";
import {
  ActionData,
  ActionSource,
  REJECT_TRANSACTION_OPTIONS_BENEFICIARY_MAPPING,
  TransactionActionParsed,
  TransactionActionType,
} from "~/templates/transactions/types";
import { formatterDateTime } from "~/utils/formatterDateTime";

export const formatterDate = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});
export const formatterTime = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "numeric",
});

const manualActionsCallSources = ["dashboard", "bulk"];

const getBeneficiaryRejectedReasonText = (reason: string) => {
  const beneficiaryReason = REJECT_TRANSACTION_OPTIONS_BENEFICIARY_MAPPING[reason];
  return beneficiaryReason || reason;
};

const getReasonByCallSource = (callSource: ActionSource | undefined) => {
  switch (callSource) {
    case "rules":
      return "by your rules";
    case "api":
      return "via API";
    default:
      return "by manual review";
  }
};

const DarkGreyLink = styled.a`
  color: #828282;
`;

const WarningNote = styled.div`
  background-color: rgb(253, 222, 225);
  padding: 15px;
  border: 1px solid rgb(250, 190, 196);
  border-radius: 5px;
`;

const ReasonText = styled.div`
  font-size: 12px;
`;

const TransactionIdText = styled.div`
  font-size: 22px;
  text-transform: capitalize;
`;

export function DetailListItemDate({
  date,
  ...props
}: { date?: Date | null } & React.ComponentProps<typeof DetailList.Item>) {
  const iso = date ? date.toISOString() : null;
  const formattedDate = date ? formatterDateTime.format(date) : null;
  const humanDate = date ? formatterDate.format(date) : "";
  const humanTime = date ? formatterTime.format(date) : "";
  return (
    <DetailList.Item as="time" dateTime={iso} title={formattedDate} {...props}>
      <DetailList.Term transform={null}>{humanDate}</DetailList.Term>
      <DetailList.Description className="localize-ignore">{humanTime}</DetailList.Description>
    </DetailList.Item>
  );
}

const getTextForAddressVerified = (action: TransactionActionParsed): string => {
  const { actionData } = action;
  const { callSource } = actionData || {};
  if (action.action === "confirm") {
    if (actionData?.callSource === "auto") {
      switch (actionData.confirmedBy) {
        case "address-book":
          return "Beneficiary blockchain address auto-identified by Address Book.";
        case "address-hash":
          return "Beneficiary blockchain address auto-identified by Network Discoverability setting.";
        case "address-webhook":
          return "Beneficiary blockchain address auto-identified by Webhook.";
        default:
          return "The ownership of the destination blockchain address has been confirmed. The transfer data has been received.";
      }
    } else if (manualActionsCallSources.includes(callSource ?? "")) {
      return "Beneficiary blockchain address manually verified by you.";
    }
  } else if (action.action === "reject") {
    return actionData?.callSource === "auto" && actionData.rejectedBy === "address-webhook"
      ? "Beneficiary blockchain address auto-rejected by Webhook."
      : "You rejected this transfer request. The transfer data has not been received.";
  }
  return "";
};

const actionsToShow: {
  incoming: TransactionActionType[];
  outgoing: TransactionActionType[];
  internal: TransactionActionType[];
} = {
  incoming: [
    TransactionActionType.CONFIRM,
    TransactionActionType.REJECT,
    TransactionActionType.DECLINE,
    TransactionActionType.NOT_READY,
    TransactionActionType.ACCEPT,
    TransactionActionType.SEND_TO_INBOX,
  ],
  outgoing: [TransactionActionType.CANCEL, TransactionActionType.APPROVE, TransactionActionType.SEND_TO_INBOX],
  internal: [],
};

export default function TransactionStatus({
  transaction,
  warnings,
  requiredFields,
  onClickOpenApprove,
  onClickOpenCancel,
  onClickOpenAccept,
  onClickOpenDecline,
}: {
  transaction: TransactionWrap;
  warnings: string[];
  requiredFields: string[];
  onClickOpenApprove?: (id: string) => void;
  onClickOpenCancel?: (id: string) => void;
  onClickOpenAccept?: (id: string) => void;
  onClickOpenDecline?: (id: string) => void;
}) {
  const {
    isNew,
    isWaiting,
    isSaved,
    isMissingBeneficiaryData,
    isWaitingForTR,
    isIncomplete,
    businessDaysSinceRequestForInfo,
  } = transaction;
  const [reason, setReason] = useState<string>();
  const vaspDID = useCurrentViewerVaspDID();
  const [vasps, vaspsLoading] = useAllVASPs("");
  const { data: vaspSettingsResponse } = useGetVaspSettings(vaspDID);
  const { statusToProcessBlockchain, requestTrackingForMissingData: hasRequestTrackingForMissingDataSetting } =
    vaspSettingsResponse || ({} as VaspSettingsPayload);
  const { data: members } = useVaspUsersInfo(vaspDID ? vaspDID : null);
  const { data: originatorVASP } = useTfSimpleVaspInfo(
    transaction && transaction.originatorVASPdid,
    ["*"],
    undefined,
    true,
    {
      dedupingInterval: 10000,
    },
  );
  const { data: beneficiaryVASP } = useTfSimpleVaspInfo(
    transaction && transaction.beneficiaryVASPdid,
    ["*"],
    undefined,
    true,
    {
      dedupingInterval: 10000,
    },
  );
  const { updateActionLoggingEnabled } = useFlags();

  const [counterVASP, , direction] = transaction?.direction({ did: vaspDID }) || [];
  const counterpartyVaspName = counterVASP?.name || "MISSING ORIGINATOR";

  const mapVaspsToNames = (vaspDids: string[]) => {
    if (vaspsLoading || !vasps) return ["..."];
    return vasps.filter((v) => vaspDids.includes(v.did)).map((v) => v.name);
  };

  const getMemberEmail = (memberDid: string, actionName: TransactionActionType) => {
    if (!actionsToShow[direction].includes(actionName)) return "";
    return members?.find((member) => member.did === memberDid)?.email;
  };

  const getRuleText = (action: TransactionActionParsed): string => {
    const actionData = action.actionData;
    let actionName = action.action.replace(/_/g, " ");
    if (actionName === "not ready") actionName = "mark as not ready to respond";
    const actionText = actionName === "send to inbox" ? "manually review" : `automatically ${actionName}`;
    if (!actionsToShow[direction].includes(action.action as TransactionActionType)) return "";
    if (actionData?.callSource === "rules") {
      if (actionData?.hasError) {
        return `An error occurred while performing ${actionData?.ruleType} check: ${actionData?.errorMessage}`;
      } else {
        switch (actionData?.ruleType) {
          case RuleCheck.JURISDICTION:
            return `If Jurisdiction is [any of the following] then ${actionText} the ${direction} transfer: ${actionData?.ruleValue
              .split(",")
              .map(getCountryNames)
              .join(", ")}`;
          case RuleCheck.CUSTOMER_JURISDICTION:
            return `If Customer Jurisdiction is [any of the following] then ${actionText} the ${direction} transfer: ${actionData?.ruleValue
              .split(",")
              .map(getCountryNames)
              .join(", ")}`;
          case RuleCheck.VASP:
            return `If VASP [is any of the following] then ${actionText} the ${direction} transfer: ${actionData?.ruleValue
              .split(",")
              .map(mapVaspsToNames)
              .join(", ")}`;
          case RuleCheck.SANCTION_LIST_CHECK:
            return `If sanction list check is ${
              actionData.ruleValue === "true" ? "a potential match" : "no match"
            } then ${actionText} the ${direction} transfer`;
          case RuleCheck.RISK_SCORE:
            return `If risk score is ${actionData.ruleValue} then ${actionText} the ${direction} transfer`;
          case RuleCheck.REVIEWED_VASP_LIST:
            return `If vasp is ${actionData.ruleValue} then ${actionText} the ${direction} transfer`;
          case RuleCheck.TRANSACTION_AMOUNT:
            // eslint-disable-next-line no-case-declarations
            const { operator, threshold } = JSON.parse(actionData?.ruleValue as string);
            return `If Transaction Amount is ${
              operator === "greaterThan" ? "greater than" : "less than"
            } ${threshold} then ${actionText} the ${direction} transfer`;
          case RuleCheck.ALL:
            return `${_.capitalize(actionText)} all ${direction} transfers`;
          default:
            return "";
        }
      }
    }
    return "";
  };

  useEffect(() => {
    const data = transaction?.actions?.find((action) =>
      ["cancel", "reject", "decline", "not_ready"].includes(action.action),
    );
    if (data && data.actionData) {
      const parsedActionData = JSON.parse(data?.actionData);
      setReason(parsedActionData.reason);
    }
  }, [transaction]);

  const getIncompleteStatusDate = () => {
    if (transaction.actions && transaction.actions?.length > 0) {
      const notifyAction = transaction.actions.find((action) => action.action === TransactionActionType.NOTIFY);
      if (notifyAction) {
        return new Date(notifyAction.createdAt);
      }
    }
    return transaction.createdAt; //this should not happen, but there is a BE bug that doesn't find the NOTIFY actions
  };

  const isOutgoing = direction === "outgoing";
  const isIncoming = direction === "incoming";

  const sortedParsedActions: TransactionActionParsed[] | undefined = transaction?.actions
    ?.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    })
    .map(
      (action) =>
        ({
          ...action,
          actionData: (action.actionData ? JSON.parse(action.actionData) : null) as ActionData | null,
        } as TransactionActionParsed),
    );

  const isTransactionCreatedViaTxCreate = sortedParsedActions?.find((parsedAction) => parsedAction.action === "create");
  const notifyActionExists = sortedParsedActions?.some((parsedAction) => parsedAction.action === "notify");
  const initiatorVasp = isTransactionCreatedViaTxCreate
    ? originatorVASP?.name || "Originator VASP"
    : beneficiaryVASP?.name || "Beneficiary VASP";

  return (
    <Grid.Container>
      <Box as={Section}>
        <Padding sized="medium">
          <TransactionDetailsHeader
            transaction={transaction}
            requiredFields={requiredFields}
            onClickOpenApprove={onClickOpenApprove}
            onClickOpenCancel={onClickOpenCancel}
            onClickOpenAccept={onClickOpenAccept}
            onClickOpenDecline={onClickOpenDecline}
          />
          <DetailList.Content>
            <DetailList.Item mobile={12} desktop={12}>
              <DetailList.Term>Transfer ID</DetailList.Term>
              <DetailList.Description>
                <span
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    navigator.clipboard.writeText(transaction.id);
                    toast(`Copied transaction ID to clipboard`, { duration: 2000 });
                  }}
                >
                  {transaction.id} <FiCopy title="Copy to clipboard" />
                </span>
              </DetailList.Description>
            </DetailList.Item>
          </DetailList.Content>
          <DetailList.Content>
            <DetailList.Item mobile={12} desktop={12}>
              <DetailList.Term>Initiated By</DetailList.Term>
              <DetailList.Description>
                <span style={{ cursor: "pointer" }}>{initiatorVasp}</span>
              </DetailList.Description>
            </DetailList.Item>
          </DetailList.Content>

          <DetailList.Header>
            <DetailList.Item mobile={12}>
              <DetailList.Title as="h2">Status</DetailList.Title>
            </DetailList.Item>
          </DetailList.Header>
          {isIncomplete && !notifyActionExists && (
            <DetailList.Content>
              <DetailList.Item mobile={12} desktop={3}>
                <DetailList.Term>Missing Data</DetailList.Term>
                {(() => {
                  if (hasRequestTrackingForMissingDataSetting) {
                    const { kind, progressBar } = getRequestStatusAndProgress(businessDaysSinceRequestForInfo ?? 0);
                    return (
                      <DetailList.Description size={7}>
                        {getRequestMessage(businessDaysSinceRequestForInfo).message}
                        {businessDaysSinceRequestForInfo !== undefined ? (
                          <Progress.Bar sized="small" value={progressBar} kind={kind} aria-label="progress-bar" />
                        ) : null}
                      </DetailList.Description>
                    );
                  }

                  return (
                    <DetailList.Description>
                      <Progress.Bar sized="small" kind="warning" value="40" aria-label="progress-bar" />
                    </DetailList.Description>
                  );
                })()}
              </DetailList.Item>

              <DetailListItemDate
                date={getIncompleteStatusDate()}
                mobile={12}
                desktop={3}
                className="localize-ignore"
              />

              <DetailList.Item mobile={12} desktop={6}>
                {isIncoming && (
                  <DetailList.Term transform={null}>
                    {hasRequestTrackingForMissingDataSetting
                      ? businessDaysSinceRequestForInfo === undefined
                        ? "Originator not reached"
                        : "No response"
                      : "Originator missing information was requested."}
                  </DetailList.Term>
                )}
                {isOutgoing && (
                  <DetailList.Term transform={null}>
                    {hasRequestTrackingForMissingDataSetting
                      ? businessDaysSinceRequestForInfo === undefined
                        ? "Missing information was requested"
                        : "No response"
                      : "Missing information was requested"}
                  </DetailList.Term>
                )}
              </DetailList.Item>
            </DetailList.Content>
          )}
          {isWaitingForTR && (
            <DetailList.Content>
              <DetailList.Item mobile={12} desktop={3}>
                <DetailList.Term>Waiting for TR</DetailList.Term>
                <DetailList.Description>
                  <Progress.Bar sized="small" kind="warning" value="40" />
                </DetailList.Description>
              </DetailList.Item>

              <DetailListItemDate
                date={getIncompleteStatusDate()}
                mobile={12}
                desktop={3}
                className="localize-ignore"
              />

              <DetailList.Item mobile={12} desktop={6}>
                <DetailList.Term transform={null}>Requires originator data</DetailList.Term>
              </DetailList.Item>
            </DetailList.Content>
          )}
          {isMissingBeneficiaryData && (
            <DetailList.Content>
              <DetailList.Item mobile={12} desktop={3}>
                <DetailList.Term>Missing Beneficiary Data</DetailList.Term>
                <DetailList.Description>
                  <Progress.Bar sized="small" kind="warning" value="40" />
                </DetailList.Description>
              </DetailList.Item>

              <DetailListItemDate
                date={transaction.lastActionDate}
                mobile={12}
                desktop={3}
                className="localize-ignore"
              />

              <DetailList.Item mobile={12} desktop={6}>
                <DetailList.Term transform={null}>
                  This transfer is missing information about its beneficiary
                </DetailList.Term>
              </DetailList.Item>
            </DetailList.Content>
          )}
          {isWaiting && (
            <DetailList.Content>
              <DetailList.Item mobile={12} desktop={3}>
                <DetailList.Term>Pending Contact Information</DetailList.Term>
                <DetailList.Description>
                  <Progress.Bar sized="small" kind="warning" value="66" />
                </DetailList.Description>
              </DetailList.Item>

              <DetailListItemDate
                date={transaction.lastActionDate}
                mobile={12}
                desktop={3}
                className="localize-ignore"
              />

              <DetailList.Item mobile={12} desktop={6}>
                <DetailList.Term transform={null}>Requires additional information</DetailList.Term>
              </DetailList.Item>
            </DetailList.Content>
          )}
          {sortedParsedActions &&
            sortedParsedActions.map((action, actionIndex) => {
              const { value, type } = getProgress({ action: action.action as TransactionActionType });

              const wasTriggeredByRules = action.actionData?.callSource === "rules";
              const wasTriggeredManually = manualActionsCallSources.includes(action.actionData?.callSource ?? "");
              const callSource = action.actionData?.callSource;
              const actionName = action.action as TransactionActionType;
              const userEmail = getMemberEmail(action.createdBy, actionName);
              const wasManualReviewTriggeredByRules =
                ((actionName === TransactionActionType.CREATE && isOutgoing) ||
                  (actionName === TransactionActionType.APPROVE && isIncoming)) &&
                sortedParsedActions?.[Math.abs(actionIndex - 1)]?.action === "send_to_inbox";
              const sendToInboxAction = wasManualReviewTriggeredByRules
                ? sortedParsedActions?.[Math.abs(actionIndex - 1)]
                : null;
              if (
                (actionName === TransactionActionType.APPROVE ||
                  actionName === TransactionActionType.ACCEPT ||
                  actionName === TransactionActionType.NOTIFY) &&
                isSaved
              ) {
                return (
                  <DetailList.Content key={action.hash}>
                    <DetailList.Item mobile={12} tablet={6} desktop={3}>
                      <DetailList.Term>Stored</DetailList.Term>
                      <DetailList.Description>
                        <Progress.Bar sized="small" kind="success" value="100" />
                      </DetailList.Description>
                    </DetailList.Item>

                    <DetailListItemDate
                      date={new Date(action.createdAt)}
                      mobile={12}
                      desktop={3}
                      className="localize-ignore"
                    />

                    <DetailList.Item mobile={12} tablet={6} desktop={6}>
                      {isIncoming && <DetailList.Term transform={null}>The transfer has been stored.</DetailList.Term>}
                      {isOutgoing && <DetailList.Term transform={null}>The transfer has been stored.</DetailList.Term>}
                    </DetailList.Item>
                  </DetailList.Content>
                );
              } else if (
                [
                  TransactionActionType.REJECT,
                  TransactionActionType.DECLINE,
                  TransactionActionType.NOT_READY,
                  TransactionActionType.ACCEPT,
                  TransactionActionType.CONFIRM,
                  TransactionActionType.CREATE,
                  TransactionActionType.APPROVE,
                  TransactionActionType.CANCEL,
                  TransactionActionType.NOTIFY,
                ].includes(actionName)
              ) {
                return (
                  <DetailList.Content key={action.hash}>
                    <DetailList.Item mobile={12} desktop={3}>
                      <DetailList.Term id="transactionStatus">{getActionText(actionName, direction)}</DetailList.Term>
                      {(() => {
                        if (
                          hasRequestTrackingForMissingDataSetting &&
                          isIncomplete &&
                          businessDaysSinceRequestForInfo !== undefined
                        ) {
                          const { kind, progressBar } = getRequestStatusAndProgress(businessDaysSinceRequestForInfo);

                          return (
                            <DetailList.Description size={7}>
                              {getRequestMessage(businessDaysSinceRequestForInfo).message}
                              <Progress.Bar sized="small" value={progressBar} kind={kind} aria-label="progress-bar" />
                            </DetailList.Description>
                          );
                        }

                        return (
                          <DetailList.Description>
                            <Progress.Bar sized="small" kind={type} value={value} aria-label="progress-bar" />
                          </DetailList.Description>
                        );
                      })()}
                    </DetailList.Item>

                    <DetailListItemDate
                      date={new Date(action.createdAt)}
                      mobile={12}
                      desktop={3}
                      className="localize-ignore"
                    />

                    <DetailList.Item mobile={12} desktop={6}>
                      {isOutgoing && actionName === TransactionActionType.CANCEL && (
                        <>
                          <DetailList.Term transform={null}>
                            {`Transfer cancelled ${getReasonByCallSource(callSource)}`}
                          </DetailList.Term>
                          {reason && <ReasonText>{`Reason: ${reason} `}</ReasonText>}
                        </>
                      )}

                      {isIncoming && actionName === TransactionActionType.NOTIFY && (
                        <DetailList.Term transform={null}>
                          {hasRequestTrackingForMissingDataSetting
                            ? businessDaysSinceRequestForInfo === undefined
                              ? "Originator not reached"
                              : "No response"
                            : "Originator missing information was requested."}
                        </DetailList.Term>
                      )}
                      {isOutgoing && actionName === TransactionActionType.NOTIFY && (
                        <DetailList.Term transform={null}>
                          {hasRequestTrackingForMissingDataSetting
                            ? "No response"
                            : "Missing information was requested"}
                        </DetailList.Term>
                      )}

                      {isIncoming && actionName === TransactionActionType.CANCEL && (
                        <>
                          <DetailList.Term transform={null}>{`Transfer cancelled by originator VASP`}</DetailList.Term>
                          {reason && <ReasonText>{`Reason: ${reason} `}</ReasonText>}
                        </>
                      )}
                      {isIncoming && actionName === TransactionActionType.REJECT && (
                        <>
                          <DetailList.Term transform={null}>{getTextForAddressVerified(action)}</DetailList.Term>
                          {reason && <ReasonText>{`Reason: ${reason} `}</ReasonText>}
                        </>
                      )}
                      {isOutgoing && actionName === TransactionActionType.REJECT && (
                        <>
                          <DetailList.Term transform={null}>
                            {`${beneficiaryVASP?.name || "Beneficiary VASP"} rejected this transfer.`}
                          </DetailList.Term>
                          {reason && <ReasonText>{`Reason: ${getBeneficiaryRejectedReasonText(reason)} `}</ReasonText>}
                        </>
                      )}
                      {isIncoming && actionName === TransactionActionType.NOT_READY && (
                        <>
                          <DetailList.Term transform={null}>
                            {`Transfer marked as "Not ready to respond" ${getReasonByCallSource(callSource)}`}
                          </DetailList.Term>
                          {reason && <ReasonText>{`Reason: ${reason} `}</ReasonText>}
                        </>
                      )}
                      {isOutgoing && actionName === TransactionActionType.NOT_READY && (
                        <>
                          <DetailList.Term transform={null}>
                            {`${
                              beneficiaryVASP?.name || "Beneficiary VASP"
                            } marked this transfer as "Not ready to respond"`}
                          </DetailList.Term>
                          {reason && <ReasonText>{`Reason: ${reason} `}</ReasonText>}
                        </>
                      )}
                      {isIncoming && actionName === TransactionActionType.ACCEPT && (
                        <DetailList.Term transform={null}>
                          {`Transfer accepted ${getReasonByCallSource(callSource)}`}
                        </DetailList.Term>
                      )}
                      {isOutgoing && actionName === TransactionActionType.ACCEPT && (
                        <DetailList.Term transform={null}>
                          {beneficiaryVASP?.name || "Beneficiary VASP"} accepted the transfer.
                        </DetailList.Term>
                      )}
                      {isIncoming && actionName === TransactionActionType.DECLINE && (
                        <>
                          <DetailList.Term transform={null}>
                            {`Transfer declined ${getReasonByCallSource(callSource)}`}
                          </DetailList.Term>
                          {reason && <ReasonText>{`Reason: ${reason} `}</ReasonText>}
                        </>
                      )}
                      {isOutgoing && actionName === TransactionActionType.DECLINE && (
                        <>
                          <DetailList.Term transform={null}>
                            {beneficiaryVASP?.name || "Beneficiary VASP"} declined the transfer.
                          </DetailList.Term>
                          {reason && <ReasonText>{`Reason: ${reason} `}</ReasonText>}
                        </>
                      )}
                      {isIncoming && actionName === TransactionActionType.CONFIRM && (
                        <div data-intercom-target="accept-or-decline-section-tourid-447932">
                          <DetailList.Term transform={null}>{getTextForAddressVerified(action)}</DetailList.Term>
                        </div>
                      )}
                      {isOutgoing && actionName === TransactionActionType.CONFIRM && (
                        <DetailList.Term transform={null}>
                          {beneficiaryVASP?.name || "Beneficiary VASP"} received the transfer request.
                        </DetailList.Term>
                      )}
                      {isIncoming && actionName === TransactionActionType.CREATE && (
                        <DetailList.Term
                          transform={null}
                        >{`${counterpartyVaspName} created a transfer.`}</DetailList.Term>
                      )}
                      {isOutgoing && actionName === TransactionActionType.CREATE && (
                        <DetailList.Term transform={null}>
                          {wasManualReviewTriggeredByRules && sendToInboxAction
                            ? `You created a transfer and manual review was triggered by your rules:`
                            : `You created a transfer.`}
                        </DetailList.Term>
                      )}
                      {isIncoming && actionName === TransactionActionType.APPROVE && (
                        <DetailList.Term transform={null}>
                          {wasManualReviewTriggeredByRules && sendToInboxAction
                            ? `You received a transfer request from ${counterpartyVaspName} and manual review was triggered by your rules:`
                            : `You received a transfer request from ${counterpartyVaspName}.`}
                        </DetailList.Term>
                      )}
                      {isOutgoing && actionName === TransactionActionType.APPROVE && (
                        <DetailList.Term transform={null}>
                          {`Transfer request approved  ${getReasonByCallSource(callSource)}`}
                        </DetailList.Term>
                      )}
                      {(wasTriggeredByRules || wasManualReviewTriggeredByRules) && (
                        <ReasonText>
                          {getRuleText(
                            wasManualReviewTriggeredByRules && sendToInboxAction ? sendToInboxAction : action,
                          )}
                        </ReasonText>
                      )}

                      {wasTriggeredManually && userEmail && <ReasonText>By {userEmail}</ReasonText>}
                    </DetailList.Item>
                  </DetailList.Content>
                );
              } else if (actionName === TransactionActionType.REDIRECT) {
                if (isIncoming) return null;
                const actionData = action.actionData;
                return (
                  <DetailList.Content key={action.hash}>
                    <DetailList.Item mobile={12} desktop={3}>
                      <DetailList.Term>Redirected</DetailList.Term>
                      <DetailList.Description>
                        <Progress.Bar sized="small" kind="success" value="33" />
                      </DetailList.Description>
                    </DetailList.Item>

                    <DetailListItemDate
                      date={new Date(action.createdAt)}
                      mobile={12}
                      desktop={3}
                      className="localize-ignore"
                    />

                    <DetailList.Item mobile={12} desktop={6}>
                      <DetailList.Term
                        transform={null}
                      >{`Redirected from ${actionData?.beneficiaryVASPFrom} to ${actionData?.beneficiaryVASPTo}`}</DetailList.Term>
                    </DetailList.Item>
                  </DetailList.Content>
                );
              } else if (actionName === TransactionActionType.UPDATE && updateActionLoggingEnabled) {
                const actionData = action.actionData;
                return (
                  <DetailList.Content key={action.hash}>
                    <DetailList.Item mobile={12} desktop={3}>
                      <DetailList.Term>Updated</DetailList.Term>
                    </DetailList.Item>

                    <DetailListItemDate
                      date={new Date(action.createdAt)}
                      mobile={12}
                      desktop={3}
                      className="localize-ignore"
                    />

                    <DetailList.Item mobile={12} desktop={6}>
                      <DetailList.Term transform={null}>{`Updated by ${actionData?.updatedBy || action.createdBy}${
                        actionData?.updatedFields &&
                        actionData.updatedFields.length > 0 &&
                        `: ${actionData.updatedFields.join(", ")}`
                      }`}</DetailList.Term>
                    </DetailList.Item>
                  </DetailList.Content>
                );
              } else if (actionName === TransactionActionType.RECEIVE) {
                return (
                  <DetailList.Content key={action.hash}>
                    <DetailList.Item mobile={12} desktop={3}>
                      <DetailList.Term>Waiting for TR</DetailList.Term>
                      <DetailList.Description>
                        <Progress.Bar sized="small" kind="success" value="33" />
                      </DetailList.Description>
                    </DetailList.Item>

                    <DetailListItemDate
                      date={new Date(action.createdAt)}
                      mobile={12}
                      desktop={3}
                      className="localize-ignore"
                    />

                    <DetailList.Item mobile={12} desktop={6}>
                      <DetailList.Term transform={null}>{`Waiting for travel rule data`}</DetailList.Term>
                    </DetailList.Item>
                  </DetailList.Content>
                );
              } else {
                return null;
              }
            })}
          {isNew && (
            <DetailList.Content>
              <DetailList.Item mobile={12} desktop={12}>
                {warnings.filter((x: string) => !x.startsWith("optional-"))?.length > 0 && (
                  <WarningNote>
                    <strong>Note</strong>
                    <br />
                    This transfer is missing data required in the Beneficiary VASPs jurisdiction. If sent, it might be
                    automatically declined by the Beneficiary VASP.
                  </WarningNote>
                )}
              </DetailList.Item>
            </DetailList.Content>
          )}
          {statusToProcessBlockchain && (
            <AuthrorisedBlockchainTransactionsBanner statusToProcessBlockchain={statusToProcessBlockchain} />
          )}
          {transaction.blockchainTransactionTimestamp && (
            <DetailList.Content>
              <DetailList.Item mobile={12} desktop={3}>
                <DetailList.Term>Blockchain transaction</DetailList.Term>
                <DetailList.Description>
                  <Progress.Bar sized="small" kind="success" value="0" />
                </DetailList.Description>
              </DetailList.Item>

              <DetailListItemDate
                date={new Date(transaction.blockchainTransactionTimestamp)}
                mobile={12}
                desktop={3}
                className="localize-ignore"
              />

              <DetailList.Item mobile={12} desktop={6}>
                <DetailList.Term transform={null}>The time when the blockchain transaction took place.</DetailList.Term>
              </DetailList.Item>
            </DetailList.Content>
          )}
        </Padding>
      </Box>
    </Grid.Container>
  );
}

export const TransactionDetailsHeader = ({
  transaction,
  requiredFields,
  onClickOpenApprove,
  onClickOpenCancel,
  onClickOpenAccept,
  onClickOpenDecline,
}: {
  transaction: TransactionWrap;
  requiredFields: string[];
  onClickOpenApprove?: (id: string) => void;
  onClickOpenCancel?: (id: string) => void;
  onClickOpenAccept?: (id: string) => void;
  onClickOpenDecline?: (id: string) => void;
}) => {
  const {
    isCancelled,
    isRejected,
    isDeclined,
    isNew,
    isWaiting,
    isSent,
    isSaved,
    isAccepted,
    isIncomplete,
    isMissingBeneficiaryData,
    isWaitingForTR,
    isNotReady,
    isAcknowledged,
  } = transaction;
  const vaspDID = useCurrentViewerVaspDID();

  const [, , direction] = transaction?.direction({ did: vaspDID }) || [];

  const viewerPermissions = useCurrentViewerPermissions();
  const hasPermissionToAccept = viewerPermissions.includes(ViewerPermissions["transactions.accept"]);
  const hasPermissionToDeclineOrReject = viewerPermissions.includes(ViewerPermissions["transactions.decline"]);

  const isComplete = isAcknowledged || isSaved;

  const isOutgoing = direction === "outgoing";
  const isIncoming = direction === "incoming";
  return (
    <div style={{ display: "flex", gap: 16, justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap" }}>
      <TransactionIdText>
        <b>{direction} Transfer</b>{" "}
      </TransactionIdText>
      <div style={{ display: "flex", gap: 20, justifyContent: "flex-end", paddingTop: 4 }}>
        <HasPermission permission={ViewerPermissions["transactions.cancel"]}>
          {isOutgoing && (isNew || isWaiting || isMissingBeneficiaryData || isSent) && (
            <DetailList.Description>
              <ClickableText
                onClick={() => onClickOpenCancel?.(transaction.id)}
                weight="bolder"
                color="danger"
                disabled={isComplete || isRejected || isDeclined || isCancelled || isNotReady}
              >
                Cancel transfer
              </ClickableText>
            </DetailList.Description>
          )}
        </HasPermission>

        <HasPermission permission={ViewerPermissions["transactions.approve"]}>
          {isOutgoing && isNew && (
            <DetailList.Description>
              <ClickableText
                onClick={() => onClickOpenApprove?.(transaction.id)}
                weight="bolder"
                color="success"
                disabled={isSent || isComplete || isRejected || isDeclined || isCancelled || isWaiting}
              >
                Approve and send
              </ClickableText>
            </DetailList.Description>
          )}
        </HasPermission>
        <HasPermission permission={ViewerPermissions["transactions.update"]}>
          {(isWaitingForTR || isIncomplete || isMissingBeneficiaryData || isWaiting) && direction !== "internal" && (
            <>
              <DetailList.Description size="7" weight="bolder">
                <Link href={`/dashboard/transactions/${transaction.id}/edit`} passHref>
                  <DarkGreyLink style={{ fontSize: 16 }}>Update data</DarkGreyLink>
                </Link>
              </DetailList.Description>
            </>
          )}
        </HasPermission>

        {isIncoming && isAcknowledged && hasPermissionToAccept && hasPermissionToDeclineOrReject && (
          <>
            <DetailList.Description
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <ClickableText
                onClick={() => onClickOpenDecline?.(transaction.id)}
                weight="bolder"
                color="danger"
                disabled={isAccepted || isRejected || isCancelled || isDeclined}
              >
                Decline transfer
              </ClickableText>
            </DetailList.Description>
            <DetailList.Description>
              <ClickableText
                onClick={() => onClickOpenAccept?.(transaction.id)}
                weight="bolder"
                color="success"
                disabled={isSent || isAccepted || isRejected || isCancelled || isDeclined || isWaiting}
              >
                Accept transfer
              </ClickableText>
            </DetailList.Description>
            {!requiredFields?.every((field) => !!((transaction as unknown) as Record<string, string>)[field]) && (
              <BoxDanger style={{ borderRadius: 5, padding: 10, marginTop: 10 }}>
                <b>Note:</b> This transfer is missing data required in your jurisdiction
              </BoxDanger>
            )}
          </>
        )}
      </div>
    </div>
  );
};
